import React from 'react';
import AdminLayout from '../components/AdminLayout';

/**
 * Analytics Page
 * Admin page to view platform analytics and insights
 * Route: /admin/analytics
 */
const Analytics: React.FC = () => {
    return (
        <AdminLayout
            showHeader={true}
            headerTitle="Analytics"
            headerSubtitle="Platform analytics, insights, and performance metrics"
        >
            <div>
                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-2xl p-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-[#015FA6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-2xl font-bold text-[#0B111E]">1,234</p>
                        <p className="text-sm text-[#757575]">Total Users</p>
                        <p className="text-xs text-green-600 mt-2">↑ 12% from last month</p>
                    </div>

                    <div className="bg-white rounded-2xl p-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-2xl font-bold text-[#0B111E]">456</p>
                        <p className="text-sm text-[#757575]">Active Jobs</p>
                        <p className="text-xs text-green-600 mt-2">↑ 8% from last month</p>
                    </div>

                    <div className="bg-white rounded-2xl p-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-2xl font-bold text-[#0B111E]">789</p>
                        <p className="text-sm text-[#757575]">Applications</p>
                        <p className="text-xs text-green-600 mt-2">↑ 15% from last month</p>
                    </div>

                    <div className="bg-white rounded-2xl p-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-2xl font-bold text-[#0B111E]">123</p>
                        <p className="text-sm text-[#757575]">Companies</p>
                        <p className="text-xs text-green-600 mt-2">↑ 5% from last month</p>
                    </div>
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* User Growth Chart */}
                    <div className="bg-white rounded-2xl p-6">
                        <h2 className="text-lg font-semibold text-[#0B111E] mb-4">User Growth</h2>
                        <div className="h-64 flex items-center justify-center border-2 border-dashed border-[#CBCBCB] rounded-lg">
                            <div className="text-center text-[#757575]">
                                <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                <p className="text-sm">Chart will be displayed here</p>
                            </div>
                        </div>
                    </div>

                    {/* Job Postings Chart */}
                    <div className="bg-white rounded-2xl p-6">
                        <h2 className="text-lg font-semibold text-[#0B111E] mb-4">Job Postings Trend</h2>
                        <div className="h-64 flex items-center justify-center border-2 border-dashed border-[#CBCBCB] rounded-lg">
                            <div className="text-center text-[#757575]">
                                <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                </svg>
                                <p className="text-sm">Chart will be displayed here</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white rounded-2xl p-6 mt-6">
                    <h2 className="text-lg font-semibold text-[#0B111E] mb-4">Recent Platform Activity</h2>
                    <div className="text-center py-8 text-[#757575]">
                        <p className="text-sm">No recent activity to display</p>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default Analytics;
